import { useTranslation } from 'react-i18next';

import { EmptyState, Spinner } from '../../components';
import { ClipboardCheckIcon } from '../../layout/icons';
import { taskTitleOf, vnDate, type SubmissionTask } from './submissions';
import { useClassSubmissions } from './useClassSubmissions';
import { useMsg } from '../../i18n/msg';
import '../../styles/submissions.css';

export interface SubmissionsPanelProps {
  classTitle: string;
  /** Opens the task's đề as a tab, its Kết quả beside it. */
  onOpen: (task: SubmissionTask, courseTitle: string) => void;
  /** The task whose tab is on the board. */
  activeTaskId?: string | null;
}

// Bài tập: what this period's class handed in on IZTeach; a row puts its đề on the board with the results
export function SubmissionsPanel({ classTitle, onOpen, activeTaskId }: SubmissionsPanelProps) {
  const { t } = useTranslation(['board', 'common']);
  const tr = useMsg();
  const { roster, rosterLoading, loading, error, tasks, courseTitles, reload } = useClassSubmissions();

  if (roster.length === 0) {
    if (rosterLoading) return <Spinner label={t('submissionsPanel.loadingRoster')} />;
    return <EmptyState icon={<ClipboardCheckIcon size={26} />} title={t('submissionsPanel.noRoster')} description={t('submissionsPanel.noRosterHint')} />;
  }
  if (loading && tasks.length === 0) return <Spinner label={t('submissionsPanel.loading')} />;
  if (error) return <EmptyState icon={<ClipboardCheckIcon size={26} />} title={t('submissionsPanel.loadFailed')} description={error} action={<button type="button" className="secondary-button" onClick={reload}>{t('common:actions.retry')}</button>} />;
  if (tasks.length === 0) return <EmptyState icon={<ClipboardCheckIcon size={26} />} title={t('submissionsPanel.empty')} description={classTitle ? t('submissionsPanel.emptyHint', { class: classTitle }) : t('submissionsPanel.emptyHintNoClass')} action={<button type="button" className="secondary-button" onClick={reload}>{t('submissionsPanel.reload')}</button>} />;

  const years: string[] = [];
  tasks.forEach((item) => { if (years.indexOf(item.schoolYear) < 0) years.push(item.schoolYear); });
  return (
    <div className="hw-panel">
      <div className="hw-panel__bar">
        <p>{classTitle ? t('submissionsPanel.barHint', { class: classTitle }) : t('submissionsPanel.barHintNoClass')}</p>
        <button type="button" className="hw-panel__refresh" disabled={loading} onClick={reload}>{loading ? t('common:states.loading') : t('submissionsPanel.reload')}</button>
      </div>
      {years.map((year) => (
        <section key={year || 'none'} className="hw-panel__year">
          <h3>{year ? t('submissionsPanel.schoolYear', { year }) : t('submissionsPanel.unknownYear')}</h3>
          <ul className="up-list">
            {tasks.filter((item) => item.schoolYear === year).map((item) => (
              <li key={item.id}>
                <button type="button" className={item.id === activeTaskId ? 'up-list__row is-selected' : 'up-list__row'} aria-current={item.id === activeTaskId ? 'true' : undefined} onClick={() => onOpen(item, courseTitles[item.courseId] || '')}>
                  <span className="up-list__mark" aria-hidden={true}><ClipboardCheckIcon size={18} /></span>
                  <span className="up-list__text">
                    <span className="up-list__name" title={tr(taskTitleOf(item.title))}>{tr(taskTitleOf(item.title))}</span>
                    <span className="up-list__meta">{[t('submissionsPanel.submittedOf', { count: item.count, total: roster.length }), item.latestAt ? t('submissionsPanel.latest', { date: vnDate(item.latestAt) }) : ''].filter(Boolean).join(' • ')}</span>
                    {courseTitles[item.courseId] ? <span className="up-list__meta" title={courseTitles[item.courseId]}>{courseTitles[item.courseId]}</span> : null}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
